import React, { useEffect, useRef, useState } from "react";
import { getImpersonationSession, endImpersonation, clampToMaxTimeout } from "../../helpers/impersonationHelper";
import { USER_INFO_KEY } from "../../constants/common";

const BANNER_HEIGHT_VAR = "--impersonation-banner-height";

// Persistent marker for a session started via admin-dashboard
// impersonation (Gitcha-Admin-Frontend's Sign In As action) - without
// this, an impersonated session is visually identical to the real user's
// own session, which is how a support admin ends up acting as a customer
// by accident. Renders nothing for a normal session.
const ImpersonationBanner = () => {
  const [session, setSession] = useState(() => getImpersonationSession());
  const bannerRef = useRef(null);

  useEffect(() => {
    // Catches login/logout in another tab of the same browser. Filtered
    // to USER_INFO_KEY so an unrelated write elsewhere in the app
    // doesn't force a re-render, matching useSSOCrossTabSync's guard.
    const handleStorageChange = (e) => {
      if (e.key !== USER_INFO_KEY) return;
      setSession(getImpersonationSession());
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!session?.expiresAt) return undefined;

    // Impersonated sessions carry no refresh token, so nothing else in
    // the app notices when one expires (see getTokenExpiryMs in
    // impersonationHelper.js). Self-ends the session at expiry instead
    // of leaving the banner claiming a live session that now 401s on
    // every call. { silent: true } because this is the session dying on
    // its own, not an admin clicking "End Session" - see endImpersonation.
    //
    // endImpersonation() only touches localStorage - it doesn't re-render
    // this component, and a same-tab write never fires a "storage" event
    // to catch it another way. Without explicitly clearing session state
    // here, the banner keeps claiming a live session (and "End Session"
    // silently does nothing, since endImpersonation is idempotent) until
    // something else happens to reload the page.
    const endSilently = () => {
      if (endImpersonation({ silent: true })) {
        setSession(null);
      }
    };

    const msRemaining = session.expiresAt - Date.now();
    if (msRemaining <= 0) {
      endSilently();
      return undefined;
    }
    const timer = setTimeout(endSilently, clampToMaxTimeout(msRemaining));
    return () => clearTimeout(timer);
  }, [session?.expiresAt]);

  // Several layouts anchor fixed-position chrome to the viewport top
  // (e.g. HeaderSidebar's .top-header, BLS-tenant only) and can't rely on
  // this banner's own sticky flow to push them down. Publish the
  // rendered height as a CSS var so those layouts can offset by it
  // instead of overlapping it. Only acts while a session is showing -
  // main.scss's :root default already covers the height for every normal
  // (never-impersonated) render, and the cleanup below covers resetting
  // it back to 0 once an impersonated session ends.
  useEffect(() => {
    if (!session || !bannerRef.current) return undefined;

    const el = bannerRef.current;
    const publishHeight = () =>
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);

    publishHeight();

    // No ResizeObserver in a very old browser - keep the one-shot height
    // from above and still run the cleanup below so the var doesn't get
    // stuck non-zero after the session ends.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publishHeight) : null;
    observer?.observe(el);

    return () => {
      observer?.disconnect();
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, "0px");
    };
  }, [session]);

  if (!session) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      role="status"
      className="d-flex justify-content-center align-items-center flex-wrap gap-2 py-2 px-3"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1050,
        backgroundColor: "#fff3cd",
        color: "#664d03",
        borderBottom: "1px solid #ffe69c",
        fontSize: "14px",
      }}
    >
      <span>
        Signed in as <strong>{session.userName}</strong>. Some actions are
        disabled during this session.
      </span>
      <button
        type="button"
        onClick={() => endImpersonation()}
        className="btn btn-sm btn-outline-dark py-0 px-2"
      >
        End Session
      </button>
    </div>
  );
};

export default ImpersonationBanner;
